/**
 * Entitlement Service - Client-side API for quota and plan status
 *
 * Fetches user's current plan, quota usage, and handles upgrade prompts
 */

import axios from 'axios';
import { resolveComparisonApiBase } from './comparison';
import { auth } from '../lib/firebase/config';

const API_BASE = resolveComparisonApiBase();

export interface PlanInfo {
  code: 'anonymous' | 'edge' | 'flow' | 'nexus';
  displayName: string;
  maxComparisonsPerDay: number;
  maxModelsPerComparison: number;
  maxSavedComparisons: number;
  historyRetentionDays: number;
  hasAdvancedAnalysis: boolean;
  monthlyAnalysisBudgetMultiplier: number;
}

export interface QuotaInfo {
  used: number;
  limit: number;
  remaining: number;
  allowed: boolean;
}

export interface SavedComparisonsQuota {
  count: number;        // Total comparisons stored (not favorites)
  limit: number;        // Max total comparisons user can store
  remaining: number;
  allowed: boolean;
  percentage: number;
}

export interface TokenQuota {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  allowed: boolean;
}

export interface EntitlementStatus {
  paywallEnabled: boolean;
  plan: PlanInfo;
  quotas: {
    comparisons: QuotaInfo;
    savedComparisons?: SavedComparisonsQuota;
    tokens?: TokenQuota;
  };
  totalComparisonsCount?: number; // Total comparisons stored in database
  userTimezone?: string; // IANA timezone string (e.g., 'America/New_York')
  upgradeUrl: string;
}

export interface QuotaError {
  error: 'QUOTA_EXCEEDED';
  message: string;
  current: {
    used: number;
    limit: number;
    remaining: number;
  };
  plan: PlanInfo;
  upgradeUrl: string;
}

class EntitlementService {
  private readonly CACHE_KEY = 'sp_entitlement_cache';
  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Get auth headers with Firebase token if user is logged in
   */
  private async getAuthHeaders() {
    try {
      const user = auth.currentUser;
      if (user) {
        // FORCE TOKEN REFRESH to ensure we have a valid, non-expired token
        // Firebase tokens expire after 1 hour, so we refresh on every entitlement check
        const token = await user.getIdToken(true);
        return {
          'Authorization': `Bearer ${token}`,
        };
      }
      // If no user, log explicitly
      console.warn('[EntitlementService] No authenticated user found');
      return {};
    } catch (error) {
      console.error('[EntitlementService] Error getting auth token:', error);
      // Don't silently fail - throw to caller so they can handle appropriately
      throw error;
    }
  }

  /**
   * Get cached entitlement status (synchronous)
   * Returns null if no cache or cache is expired
   */
  getCachedStatusSync(): EntitlementStatus | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age < this.CACHE_TTL_MS) {
        console.log('[EntitlementService] Using cached status (age:', Math.round(age / 1000), 'seconds)');
        return data;
      }

      console.log('[EntitlementService] Cache expired (age:', Math.round(age / 1000), 'seconds)');
      return null;
    } catch (error) {
      console.error('[EntitlementService] Error reading cache:', error);
      return null;
    }
  }

  /**
   * Save entitlement status to cache
   */
  private cacheStatus(status: EntitlementStatus): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify({
        data: status,
        timestamp: Date.now()
      }));
      console.log('[EntitlementService] Cached status for', status.plan?.displayName || 'unknown plan');
    } catch (error) {
      console.error('[EntitlementService] Error saving cache:', error);
    }
  }

  /**
   * Clear cached entitlement status
   */
  clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      console.log('[EntitlementService] Cache cleared');
    } catch (error) {
      console.error('[EntitlementService] Error clearing cache:', error);
    }
  }

  /**
   * Get current entitlement status (plan + quotas)
   */
  async getStatus(): Promise<EntitlementStatus> {
    try {
      const headers = await this.getAuthHeaders();
      console.log('[EntitlementService] Fetching status from:', `${API_BASE}/entitlements/status`, {
        hasAuthToken: !!headers.Authorization,
      });
      const response = await axios.get(`${API_BASE}/entitlements/status`, {
        headers,
        withCredentials: true, // Include cookies for anonymous tracking
      });
      console.log('[EntitlementService] Status response:', response.data);

      // If server returns minimal response (paywall disabled), use unlimited defaults
      if (!response.data.plan || !response.data.quotas) {
        console.log('[EntitlementService] Server returned minimal response (paywall disabled) - using unlimited defaults');
        const unlimitedDefaults = this.getUnlimitedDefaults();
        // Cache the unlimited defaults
        this.cacheStatus(unlimitedDefaults);
        return unlimitedDefaults;
      }

      // Cache the full response
      this.cacheStatus(response.data);

      return response.data;
    } catch (error: any) {
      console.error('[EntitlementService] Error fetching entitlement status:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        method: error?.config?.method,
      });
      // Return unlimited defaults on error (fail-open for better UX)
      console.log('[EntitlementService] Returning unlimited defaults due to error');
      return this.getUnlimitedDefaults();
    }
  }

  /**
   * Get debug information about test user configuration and entitlements
   * Useful for troubleshooting entitlement issues
   */
  async getDebugInfo(): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      console.log('[EntitlementService] Fetching debug info from:', `${API_BASE}/entitlements/debug`, {
        hasAuthToken: !!headers.Authorization,
      });
      const response = await axios.get(`${API_BASE}/entitlements/debug`, {
        headers,
        withCredentials: true,
      });
      console.log('[EntitlementService] Debug response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[EntitlementService] Error fetching debug info:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
      });
      throw error;
    }
  }

  /**
   * Check if a quota error response indicates quota exceeded
   */
  isQuotaError(error: any): error is { response: { data: QuotaError } } {
    return (
      error?.response?.status === 403 &&
      error?.response?.data?.error === 'QUOTA_EXCEEDED'
    );
  }

  /**
   * Extract quota error details from error response
   */
  getQuotaErrorDetails(error: any): QuotaError | null {
    if (this.isQuotaError(error)) {
      return error.response.data;
    }
    return null;
  }

  /**
   * Format quota status for UI display
   */
  formatQuotaStatus(quota: QuotaInfo): string {
    if (quota.limit === 0) {
      return 'No quota available';
    }
    return `${quota.remaining} of ${quota.limit} remaining`;
  }

  /**
   * Get progress percentage for quota usage
   */
  getQuotaProgress(quota: QuotaInfo): number {
    if (quota.limit === 0) return 0;
    return Math.round((quota.used / quota.limit) * 100);
  }

  /**
   * Get color class based on quota remaining
   */
  getQuotaColor(quota: QuotaInfo): string {
    const percentage = this.getQuotaProgress(quota);
    if (percentage >= 100) return 'red'; // Exhausted
    if (percentage >= 80) return 'amber'; // Warning
    if (percentage >= 50) return 'yellow'; // Caution
    return 'green'; // Healthy
  }

  /**
   * Determine if user should see upgrade prompt
   */
  shouldShowUpgrade(status: EntitlementStatus): boolean {
    return (
      status.plan.code === 'anonymous' ||
      status.quotas.comparisons.remaining < 2
    );
  }

  /**
   * Get unlimited Nexus-like defaults when paywall is disabled or on error
   * Provides fail-open behavior for better UX
   */
  getUnlimitedDefaults(): EntitlementStatus {
    return {
      paywallEnabled: false,
      plan: {
        code: 'nexus',
        displayName: 'Nexus',
        maxComparisonsPerDay: 250,
        maxModelsPerComparison: 8,
        maxSavedComparisons: -1,
        historyRetentionDays: -1,
        hasAdvancedAnalysis: true,
        monthlyAnalysisBudgetMultiplier: 3,
      },
      quotas: {
        comparisons: {
          used: 0,
          limit: 250,
          remaining: 250,
          allowed: true,
        },
        savedComparisons: {
          count: 0,
          limit: -1,
          remaining: -1,
          allowed: true,
          percentage: 0,
        },
        tokens: {
          used: 0,
          limit: 100000,
          remaining: 100000,
          percentage: 0,
          allowed: true,
        },
      },
      userTimezone: 'UTC',
      upgradeUrl: '/pricing',
    };
  }
}

export const entitlementService = new EntitlementService();
